import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { DynamicRule, RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { DiscountRejectionCouponExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { FAILED_ATTEMPT_THRESHOLD_CONFIG } from '../../../discount-rejection-coupon.config';
import { DiscountRejectionCouponFormGroup, FailedAttemptThresholdFormGroup } from '../../../model';

@Component({
  selector: 'discount-rejection-coupon-conditions',
  templateUrl: './discount-rejection-coupon-conditions.component.html',
  styleUrls: ['./discount-rejection-coupon-conditions.component.scss'],
  standalone: false,
})
export class DiscountRejectionCouponConditionsComponent implements OnInit {
  @Input() formGroup: FormGroup<DiscountRejectionCouponFormGroup>;
  @Input() displayRules: DynamicRule[];
  @Input() displayRulesEntities: RuleEntity[];
  @Input() shopCurrencySymbol: string;

  get thresholdFormGroup(): FormGroup<FailedAttemptThresholdFormGroup> {
    return this.formGroup.controls.failedAttemptThreshold;
  }

  get attemptsControl(): FormControl<number> {
    return this.thresholdFormGroup.controls.attempts;
  }

  get minAttempts(): number {
    return FAILED_ATTEMPT_THRESHOLD_CONFIG.MIN_ATTEMPTS;
  }

  get maxAttempts(): number {
    return DiscountRejectionCouponExtension.MAX_STORED_ATTEMPTS;
  }

  get timeWindowValueControl(): FormControl<number> {
    return this.thresholdFormGroup.controls.timeWindowValue;
  }

  get isTimeWindowDisabled(): boolean {
    return (
      !this.thresholdFormGroup.controls.enabled.value || !this.thresholdFormGroup.controls.limitWithinTimeWindow.value
    );
  }

  get timeWindowMinValue(): number {
    return this.timeWindowConfigByUnit[this.thresholdFormGroup.controls.timeWindowUnit.value].min;
  }

  get timeWindowMaxValue(): number {
    return this.timeWindowConfigByUnit[this.thresholdFormGroup.controls.timeWindowUnit.value].max;
  }

  get thresholdSummaryText(): string {
    const attempts = this.thresholdFormGroup.controls.attempts.value;
    const limitWithinTimeWindow = this.thresholdFormGroup.controls.limitWithinTimeWindow.value;

    if (limitWithinTimeWindow) {
      const timeValue = this.thresholdFormGroup.controls.timeWindowValue.value;
      const unitConfig = this.timeWindowConfigByUnit[this.thresholdFormGroup.controls.timeWindowUnit.value];
      return `Trigger after ${attempts} failed attempts within ${timeValue} ${unitConfig.label}`;
    }

    return `Trigger after ${attempts} failed attempts regardless of time`;
  }

  readonly timeWindowUnitOptions: DropdownOption[] = [
    { value: DiscountRejectionCouponExtension.TimeWindowUnit.SECONDS, label: 'Seconds' },
    { value: DiscountRejectionCouponExtension.TimeWindowUnit.MINUTES, label: 'Minutes' },
  ];

  private readonly destroyRef = inject(DestroyRef);

  private readonly timeWindowConfigByUnit = {
    [DiscountRejectionCouponExtension.TimeWindowUnit.SECONDS]: {
      default: FAILED_ATTEMPT_THRESHOLD_CONFIG.DEFAULT_TIME_WINDOW_SECONDS,
      min: FAILED_ATTEMPT_THRESHOLD_CONFIG.MIN_TIME_WINDOW_SECONDS,
      max: FAILED_ATTEMPT_THRESHOLD_CONFIG.MAX_TIME_WINDOW_SECONDS,
      label: 'seconds',
    },
    [DiscountRejectionCouponExtension.TimeWindowUnit.MINUTES]: {
      default: FAILED_ATTEMPT_THRESHOLD_CONFIG.DEFAULT_TIME_WINDOW_MINUTES,
      min: FAILED_ATTEMPT_THRESHOLD_CONFIG.MIN_TIME_WINDOW_MINUTES,
      max: FAILED_ATTEMPT_THRESHOLD_CONFIG.MAX_TIME_WINDOW_MINUTES,
      label: 'minutes',
    },
  };

  ngOnInit(): void {
    this.thresholdFormGroup.controls.timeWindowUnit.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((unit) => {
        const currentValue = this.thresholdFormGroup.controls.timeWindowValue.value;
        const { min, max } = this.timeWindowConfigByUnit[unit];
        const clampedValue = Math.min(Math.max(currentValue, min), max);
        this.thresholdFormGroup.controls.timeWindowValue.setValue(clampedValue);
      });
  }
}
